// ════════════════════════════════════════════════════════════════
// COSTY — <costy-toast> Lit component (Phase B PR 6)
// ────────────────────────────────────────────────────────────────
// Transient status notifications anchored bottom-centre of the
// viewport. Replaces <fc-toast> with the redesign primitive that
// supports stacking (multiple toasts visible at once) and four
// semantic variants — success, warning, error, info — mapped onto
// the --costy-* token palette.
//
// Public surface:
//   • <costy-toast> renders a stack of notifications. The host is a
//     fixed-position container; each notification is a row inside
//     the shadow root with its own auto-dismiss timer.
//   • show(message, variant?, options?) pushes a new notification
//     and returns its id. Pass { id } in options to update an
//     existing notification in place (idempotent — handy for
//     progress / step toasts).
//   • dismiss(id) removes a notification immediately.
//
// The legacy showToast() entry point at src/toast.js re-exports
// this module's helper so the existing 'green' | 'red' | 'amber'
// API at every call site keeps working — the shim translates the
// legacy variants into the redesign palette.
// ════════════════════════════════════════════════════════════════

import { LitElement, html, css, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { TOAST_DURATION_MS } from '#lib/config.js';

export type CostyToastVariant = 'success' | 'warning' | 'error' | 'info';

/** Optional inline action button rendered alongside the message. */
export interface CostyToastAction {
  label: string;
  onClick: () => void;
}

interface ToastItem {
  id: number;
  message: string;
  variant: CostyToastVariant;
  action: CostyToastAction | null;
  // Each item owns its dismiss timer so stacks of toasts can age
  // independently.
  timer: ReturnType<typeof setTimeout> | null;
}

let _idCounter = 0;

/**
 * Stack host for transient notifications.
 *
 * @example  Imperative (preferred)
 *   import { showCostyToast } from './components/costy-toast.js';
 *   showCostyToast('Saved');
 *   showCostyToast('Network error', 'error');
 *
 * @example  Update an existing notification in place
 *   const id = showCostyToast('Uploading…', 'info');
 *   // ...later
 *   showCostyToast('Uploaded', 'success', { id });
 *
 * @example  Direct DOM
 *   <costy-toast></costy-toast>
 *   document.querySelector('costy-toast').show('Hi', 'success');
 */
export class CostyToast extends LitElement {
  static override styles = css`
    :host {
      position: fixed;
      bottom: var(--costy-space-5);
      left: 50%;
      transform: translateX(-50%);
      z-index: 9999;
      display: flex;
      flex-direction: column-reverse;
      gap: var(--costy-space-2);
      pointer-events: none;
      font-family: var(--costy-font-sans);
      max-width: min(440px, calc(100vw - var(--costy-space-5) * 2));
    }

    .item {
      pointer-events: auto;
      padding: var(--costy-space-3) var(--costy-space-4);
      border-radius: var(--costy-radius-md);
      font-size: var(--costy-text-sm);
      font-weight: var(--costy-weight-medium);
      line-height: var(--costy-leading-base);
      color: var(--costy-ink-on-accent);
      background: var(--costy-item-bg, var(--costy-success));
      box-shadow: var(--costy-shadow-lg);
      display: flex;
      align-items: center;
      gap: var(--costy-space-3);
      /* Toasts appear immediately. The fade-up entry and its
         stack-position stagger were removed 2026-09-10 — a
         confirmation the operator is waiting on shouldn't arrive on a
         delay, least of all a delay that grew with queue depth. */
    }

    .message {
      flex: 1 1 auto;
      min-width: 0;
      word-break: break-word;
    }

    button.close {
      flex: 0 0 auto;
      /* BTN-6: 24px square minimum hit target (WCAG 2.5.8), was 20px. */
      width: 24px;
      height: 24px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      border: 0;
      background: transparent;
      color: inherit;
      opacity: 0.75;
      cursor: pointer;
      border-radius: var(--costy-radius-sm);
      font-size: 16px;
      line-height: 1;
    }
    button.close:hover { opacity: 1; }
    button.close:focus-visible {
      outline: 2px solid var(--costy-ink-on-accent);
      outline-offset: 1px;
      opacity: 1;
    }

    button.action {
      flex: 0 0 auto;
      padding: var(--costy-space-2) var(--costy-space-3);
      border: 1px solid currentColor;
      background: transparent;
      color: inherit;
      font: inherit;
      font-weight: var(--costy-weight-medium);
      line-height: 1;
      cursor: pointer;
      border-radius: var(--costy-radius-sm);
      white-space: nowrap;
    }
    button.action:hover { background: rgb(255 255 255 / 0.18); }
    button.action:focus-visible {
      outline: 2px solid var(--costy-ink-on-accent);
      outline-offset: 1px;
    }

  `;

  // Variant → background token. Each notification writes the
  // resolved value into --costy-item-bg on its own element so the
  // stylesheet stays declarative.
  static readonly VARIANT_BG: Record<CostyToastVariant, string> = {
    success: 'var(--costy-success)',
    warning: 'var(--costy-warning)',
    error:   'var(--costy-danger)',
    info:    'var(--costy-info)',
  };

  // Stack of live notifications. Newest is appended; CSS reverses
  // the visual order so the most recent renders at the bottom of
  // the column (closest to the viewport edge).
  private _items: ToastItem[] = [];

  override connectedCallback(): void {
    super.connectedCallback();
    // role / aria-live on the host so screen readers announce every
    // notification as it lands, regardless of which shadow-rooted
    // item it lives inside.
    if (!this.hasAttribute('role')) this.setAttribute('role', 'status');
    if (!this.hasAttribute('aria-live')) this.setAttribute('aria-live', 'polite');
    if (!this.hasAttribute('aria-atomic')) this.setAttribute('aria-atomic', 'true');
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    for (const item of this._items) {
      if (item.timer) clearTimeout(item.timer);
    }
    this._items = [];
  }

  /**
   * Push a notification onto the stack. Returns the id assigned to
   * the new (or updated) notification. Pass `options.id` to update
   * an existing notification in place — message + variant change,
   * dismiss timer resets.
   */
  show(
    message: string,
    variant: CostyToastVariant = 'success',
    options: { id?: number; duration?: number; action?: CostyToastAction } = {},
  ): number {
    // An action toast (e.g. "reload to print") defaults to sticky so the
    // button survives long enough to be clicked; callers can still override
    // with an explicit duration. A non-positive/non-finite duration means
    // "no auto-dismiss" — the close button remains the way out.
    const duration = options.duration ?? (options.action ? 0 : TOAST_DURATION_MS);

    let item: ToastItem | undefined;
    if (options.id !== undefined) {
      item = this._items.find((i) => i.id === options.id);
    }
    if (item) {
      if (item.timer) clearTimeout(item.timer);
      item.message = message;
      item.variant = variant;
      if (options.action !== undefined) item.action = options.action;
    } else {
      item = { id: ++_idCounter, message, variant, action: options.action ?? null, timer: null };
      this._items.push(item);
    }

    const id = item.id;
    item.timer =
      duration > 0 && Number.isFinite(duration)
        ? setTimeout(() => this.dismiss(id), duration)
        : null;
    this.requestUpdate();
    // The global toast host is inert while a dialog owns focus. Mirror its
    // announcement inside the exposed dialog so validation and completion
    // messages remain available to assistive technology.
    const activeModal = [...document.querySelectorAll<HTMLElement>('#modal-container .modal-ov.open')]
      .reverse().find((modal) => !modal.hasAttribute('inert'));
    if (activeModal) {
      const container = activeModal.querySelector<HTMLElement>('.modal-box') ?? activeModal;
      let status = container.querySelector<HTMLElement>('[data-modal-toast-status]');
      if (!status) {
        status = document.createElement('span');
        status.dataset.modalToastStatus = '';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        status.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0';
        container.append(status);
      }
      status.textContent = message;
    }
    return id;
  }

  /** Remove a notification immediately. No-op if id is not live. */
  dismiss(id: number): void {
    const idx = this._items.findIndex((i) => i.id === id);
    if (idx === -1) return;
    const removed = this._items[idx];
    this._items.splice(idx, 1);
    if (removed?.timer) clearTimeout(removed.timer);
    this.requestUpdate();
  }

  /** Snapshot of live notifications. Test-only; do not mutate. */
  get items(): ReadonlyArray<{ id: number; message: string; variant: CostyToastVariant }> {
    return this._items.map(({ id, message, variant }) => ({ id, message, variant }));
  }

  private _onCloseClick = (e: Event): void => {
    const btn = e.currentTarget as HTMLElement;
    const id = Number(btn.dataset.id);
    if (!Number.isFinite(id)) return;
    this.dismiss(id);
  };

  private _onActionClick = (e: Event): void => {
    const btn = e.currentTarget as HTMLElement;
    const id = Number(btn.dataset.id);
    if (!Number.isFinite(id)) return;
    const item = this._items.find((i) => i.id === id);
    item?.action?.onClick();
    this.dismiss(id);
  };

  override render() {
    return html`
      ${repeat(
        this._items,
        (item) => item.id,
        (item) => html`
          <div
            class="item"
            part="item"
            data-variant=${item.variant}
            style=${`--costy-item-bg: ${CostyToast.VARIANT_BG[item.variant]}`}
          >
            <span class="message">${item.message}</span>
            ${item.action
              ? html`<button
                  type="button"
                  class="action"
                  data-id=${item.id}
                  @click=${this._onActionClick}
                >${item.action.label}</button>`
              : nothing}
            <button
              type="button"
              class="close"
              aria-label="Dismiss"
              data-id=${item.id}
              @click=${this._onCloseClick}
            >×</button>
          </div>
        `,
      )}
    `;
  }
}

customElements.define('costy-toast', CostyToast);

declare global {
  interface HTMLElementTagNameMap {
    'costy-toast': CostyToast;
  }
}

// ── Singleton accessor + showCostyToast() ─────────────────────────

let _instance: CostyToast | null = null;

function getOrCreate(): CostyToast | null {
  if (typeof document === 'undefined') return null;
  if (_instance && _instance.isConnected) return _instance;

  const existing = document.querySelector<CostyToast>('costy-toast');
  if (existing) {
    _instance = existing;
    return existing;
  }

  const el = document.createElement('costy-toast');
  document.body.appendChild(el);
  _instance = el;
  return el;
}

/**
 * Push a notification onto the document-level stack. Lazily inserts
 * a `<costy-toast>` host into `<body>` on first call.
 */
export function showCostyToast(
  message: string,
  variant: CostyToastVariant = 'success',
  options: { id?: number; duration?: number } = {},
): number {
  const el = getOrCreate();
  if (!el) return -1;
  return el.show(message, variant, options);
}
